<template>
  <div class="module-selection-page">
    <h1 class="module-selection-page__title">Выберите модуль расчёта</h1>
    <p class="module-selection-page__subtitle">
      Сначала выберите модуль, затем создайте или продолжите расчет курса лучевой терапии.
    </p>
    <p v-if="quotaLabel" class="module-selection-page__quota">{{ quotaLabel }}</p>
    <div v-if="resumableModule" class="module-selection-page__resume-card">
      <div>
        <p class="module-selection-page__resume-label mb-1">Продолжить работу</p>
        <p class="module-selection-page__resume-text mb-0">
          Последний модуль: <strong>{{ resumableModule.label }}</strong>
        </p>
      </div>
      <button type="button" class="btn btn-primary-modern btn-sm" @click="resumeCalculation">
        <i class="fas fa-play me-1"></i>
        Продолжить
      </button>
    </div>
    <p v-if="!hasUsableCalculationModule" class="module-selection-page__empty">
      Нет доступных модулей.
    </p>
    <div class="module-selection-page__grid">
      <button
        v-for="calcModule in visibleModules"
        :key="calcModule.id"
        type="button"
        class="module-card"
        :class="{
          'module-card--resumable': isResumableModule(calcModule.id),
          'module-card--locked': !canUseModule(calcModule.id),
        }"
        :disabled="!canUseModule(calcModule.id)"
        @click="selectModule(calcModule)"
      >
        <div class="module-card__icon">
          <i :class="calcModule.icon"></i>
        </div>
        <h3 class="module-card__title">{{ calcModule.label }}</h3>
        <p class="module-card__description">{{ calcModule.description }}</p>
        <span class="module-card__action">
          {{ moduleActionLabel(calcModule) }}
          <i v-if="canUseModule(calcModule.id)" class="fas fa-arrow-right ms-1"></i>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { calculationModules, findCalculationModuleById, isCalculationModuleAvailable } from '../config/calculationModules';
import { useCourseStore } from '../stores/courseStore';
import { useUserStore } from '../stores/userStore';
import { permissionForModule } from '@/constants/access';
import { resolveResumableCalculationModuleId, calculationModulesEqual } from '../utils/courseResumableModule';
import { courseSelectionLocation } from '@/utils/moduleCalculationNavigation';

export default {
  name: 'ModuleSelectionPage',
  data() {
    return {
      modules: calculationModules,
    };
  },
  computed: {
    courseStore() {
      return useCourseStore();
    },
    userStore() {
      return useUserStore();
    },
    quotaLabel() {
      const quota = this.userStore.quota;
      if (!quota) return '';
      if (quota.limit == null) return `Квота расчётов в ${quota.period || 'этом месяце'}: без лимита (использовано ${quota.used || 0}).`;
      const remaining = quota.remaining == null ? Math.max((quota.limit || 0) - (quota.used || 0), 0) : quota.remaining;
      return `Осталось ${remaining} из ${quota.limit} расчётов в ${quota.period || 'этом месяце'}.`;
    },
    resumableModuleId() {
      return resolveResumableCalculationModuleId(this.courseStore);
    },
    resumableModule() {
      const calcModule = findCalculationModuleById(this.resumableModuleId);
      if (!calcModule || !this.canUseModule(calcModule.id)) return null;
      return calcModule;
    },
    visibleModules() {
      return this.modules.filter((calcModule) => {
        if (!isCalculationModuleAvailable(calcModule)) return true;
        return this.canUseModule(calcModule.id);
      });
    },
    hasUsableCalculationModule() {
      return this.modules.some(
        (calcModule) => isCalculationModuleAvailable(calcModule) && this.canUseModule(calcModule.id),
      );
    },
  },
  mounted() {
    this.userStore.refreshUser();
    this.syncResumableModuleFromCourse();
  },
  methods: {
    syncResumableModuleFromCourse() {
      const moduleId = resolveResumableCalculationModuleId(this.courseStore);
      if (moduleId) {
        this.courseStore.setActiveCalculationModule(moduleId);
      }
    },
    canUseModule(moduleId) {
      if (!isCalculationModuleAvailable(moduleId)) return false;
      const code = permissionForModule(moduleId);
      if (!code) return true;
      return this.userStore.hasPermission(code);
    },
    moduleActionLabel(calcModule) {
      if (!isCalculationModuleAvailable(calcModule)) return 'Пока недоступен';
      return this.canUseModule(calcModule.id) ? 'Выбрать' : 'Нет доступа';
    },
    isResumableModule(moduleId) {
      return calculationModulesEqual(this.resumableModuleId, moduleId);
    },
    selectModule(calcModule) {
      if (!this.canUseModule(calcModule.id)) return;
      this.courseStore.setActiveCalculationModule(calcModule.id);
      this.$router.push(courseSelectionLocation(calcModule.id));
    },
    resumeCalculation() {
      if (!this.resumableModule) return;
      this.selectModule(this.resumableModule);
    },
  },
};
</script>

<style scoped>
.module-selection-page {
  max-width: 900px;
  margin: 0 auto;
}

.module-selection-page__title {
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: #212529;
}

.module-selection-page__subtitle {
  color: #6c757d;
  margin-bottom: 1.5rem;
}

.module-selection-page__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  align-items: stretch;
}

.module-selection-page__resume-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(102, 126, 234, 0.35);
  border-radius: 12px;
  background: rgba(102, 126, 234, 0.06);
}

.module-selection-page__resume-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #667eea;
  font-weight: 600;
}

.module-selection-page__resume-text {
  color: #495057;
}

.module-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  height: 100%;
  padding: 1.5rem;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  background: #fff;
  color: inherit;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.module-selection-page__quota {
  color: #495057;
  background: rgba(102, 126, 234, 0.08);
  border-radius: 8px;
  padding: 0.65rem 0.9rem;
  margin-bottom: 1.25rem;
}
.module-selection-page__empty {
  color: #6c757d;
  margin-bottom: 1rem;
}
.module-card--locked {
  opacity: 0.55;
  cursor: not-allowed;
}
.module-card--locked:hover {
  border-color: #dee2e6;
  box-shadow: none;
}

.module-card--resumable {
  border-color: rgba(102, 126, 234, 0.45);
  box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.12);
}

.module-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.module-card__icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(102, 126, 234, 0.12);
  color: #667eea;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.module-card__title {
  flex-shrink: 0;
  width: 100%;
  min-height: 2.64rem;
  font-size: 1.1rem;
  line-height: 1.2;
  margin: 0 0 0.5rem;
  color: #212529;
}

.module-card__description {
  flex: 1 1 auto;
  width: 100%;
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0 0 1rem;
  line-height: 1.4;
}

.module-card__action {
  flex-shrink: 0;
  margin-top: auto;
  font-size: 0.9rem;
  color: #667eea;
  font-weight: 500;
}

.module-card:hover .module-card__action {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .module-selection-page__grid {
    grid-template-columns: 1fr;
  }
}
</style>
